import React, { useState, useEffect, useContext, useRef } from "react";
import { makeStyles } from "@material-ui/styles";
import {
  Box,
  Button,
  Divider,
  FilledInput,
  FormControl,
  FormGroup,
  FormLabel,
  Grid,
  MenuItem,
  Select,
} from "@material-ui/core";
import { DataGrid } from "@material-ui/data-grid";
import { useLocation } from "react-router-dom";
import AuthContext from "../../context";
import { get } from "../../utils/api-services";
import helpers from "../../utils/helpers";
import ViewTitle from "../../common/view-title";
import PerimeterForm from "../components/PerimeterForm";
import XLSX from "xlsx";
import SearchForm from "../components/SearchForm";
import { Link } from "react-router-dom";
import { Tooltip, IconButton, Icon } from "@material-ui/core";

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  divider: {
    marginBottom: 10,
  },
  tableContainerTab: {
    marginTop: 20,
    width: "100%",
    overflowX: "auto",
  },
  controlTable: {
    width: "max-content",
    minWidth: "100%",
  },
  generateExcelButton: {
    backgroundColor: "#6c757d",
    borderColor: "#6c757d",
    marginLeft: 50,
    "&:hover": {
      backgroundColor: "#5a6268",
      borderColor: "#5a6268",
      boxShadow: "none",
    },
  },
  buttonsContainer: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
}));

const Control = () => {
  const { showToast, signOut, showLoader } = useContext(AuthContext);
  const classes = useStyles();
  const { pathname } = useLocation();

  const [state, setState] = useState({
    loading: true,
    data: [],
    plan_type: [],
    filter: { plan_type: 1 },
    rowSelected: {},
    perimeter: {
      date: "",
    },
  });

  const sedeName =
    (JSON.parse(localStorage.getItem("@sede") || "{}")?.name || "").toLowerCase();
  const containsOregon = sedeName.includes("oregon");
  const containsFlorida =
    sedeName.includes("florida") ||
    sedeName.includes("deland") ||
    sedeName.includes("miami");

  const toNumber = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  };

  const getSectionId = (record = {}) =>
    record?.fincasPlan?.idfinca_section ??
    record?.idfinca_section ??
    record?.section?.idfinca_section ??
    record?.fincasSection?.idfinca_section ??
    record?.fincaSection?.idfinca_section ??
    null;

  const getAreaId = (record = {}) =>
    record?.fincasPlan?.idfinca_area ??
    record?.idfinca_area ??
    record?.area?.idfinca_area ??
    null;

  const getFarmId = (record = {}, sectionsById = {}) => {
    const directFarmId =
      record?.fincasPlan?.idfinca_farm ??
      record?.idfinca_farm ??
      record?.farm?.idfinca_farm ??
      record?.area?.idfinca_farm ??
      record?.area?.farm?.idfinca_farm;

    if (directFarmId != null) return directFarmId;

    const sectionId = getSectionId(record);
    if (sectionId != null) {
      return sectionsById[String(sectionId)]?.idfinca_farm ?? null;
    }

    return null;
  };

  const getCultivationId = (record = {}, sectionsById = {}) => {
    const directCultivationId =
      record?.idfinca_cultivation_type ??
      record?.cultivationType?.idfinca_cultivation_type ??
      record?.fincasCultivationType?.idfinca_cultivation_type ??
      record?.fincaCultivationType?.idfinca_cultivation_type;

    if (directCultivationId != null) return directCultivationId;

    const sectionId = getSectionId(record);
    if (sectionId != null) {
      return sectionsById[String(sectionId)]?.idfinca_cultivation_type ?? null;
    }

    return null;
  };

  const getPlanAggregationKey = (record = {}) => {
    const plan = record?.fincasPlan || {};
    return [
      plan?.date ?? "",
      plan?.week ?? "",
      record?.resolved_farm_id ?? "",
      plan?.idfinca_plan_type ?? "",
    ].join("_");
  };

  const getProductAggregationKey = (record = {}) => {
    const plan = record?.fincasPlan || {};
    return [
      plan?.date ?? "",
      plan?.week ?? "",
      record?.resolved_farm_id ?? "",
      record?.idfinca_product ?? record?.product?.idfinca_product ?? "",
      record?.idfinca_application_mode ?? record?.applicationMode?.idfinca_application_mode ?? "",
      record?.idfinca_measure ?? record?.detailMeasure?.idfinca_measure ?? "",
    ].join("_");
  };

  const getFarmLabel = (row = {}) =>
    row?.farm_name || "";

  const getSectionLabel = (row = {}) =>
    row?.section_name || "";

  const getCultivationLabel = (row = {}) =>
    row?.cultivation_name || "";

  const getLatitude = (row = {}) =>
    row?.latitude ??
    row?.section?.latitude ??
    row?.fincasSection?.latitude ??
    row?.fincaSection?.latitude ??
    row?.fincasPlan?.section?.latitude ??
    "";

  const getLongitude = (row = {}) =>
    row?.longitude ??
    row?.section?.longitude ??
    row?.fincasSection?.longitude ??
    row?.fincaSection?.longitude ??
    row?.fincasPlan?.section?.longitude ??
    "";

  const formatDateToUS = (value) => {
    if (!value) return "";
    const dateString = String(value).trim();
    const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      return `${month}/${day}/${year}`;
    }

    const usMatch = dateString.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (usMatch) {
      return dateString;
    }

    const parsedDate = new Date(dateString);
    if (!Number.isNaN(parsedDate.getTime())) {
      const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
      const day = String(parsedDate.getDate()).padStart(2, "0");
      const year = parsedDate.getFullYear();
      return `${month}/${day}/${year}`;
    }

    return dateString;
  };

  const getDistributedAcres = (row = {}) =>
    toNumber(row?.distributed_acres ?? row?.fincasPlan?.total_acres ?? row?.total_acres);

  const getAcresPercentage = (row = {}) => {
    const distributedAcres = getDistributedAcres(row);
    const totalAcres = toNumber(row?.total_acres_all_sections);
    if (totalAcres <= 0) return 0;
    return (distributedAcres / totalAcres) * 100;
  };

  useEffect(() => {
    window.document.title = "Fincas - Application Plan";
    validatePermissions();
  }, [state.filter.plan_type]);

  const validatePermissions = () => {
    const permissions = helpers.getPermissions(pathname);
    setState((prev) => ({ ...prev, permissions }));

    permissions.read_attr
      ? makeRequest()
      : setState((prev) => ({ ...prev, loading: false }));
  };

  const makeRequest = async (queryParams = {}, filtering = false) => {
    showLoader(true);
    try {
      const [planTypeResponse, planDetailResponse, farmsResponse, sectionsResponse, areasResponse, cultivationTypesResponse] = await Promise.all([
        get("/fincas/planTypes"),
        get("/fincas/planDetails", {
          idfinca_plan_type: state.filter.plan_type || 1,
          ...queryParams,
        }),
        get("/fincas/farms"),
        get("/fincas/sections"),
        get("/fincas/areas"),
        get("/fincas/cultivationTypes"),
      ]);

      const normalizeSectionRows = (records = []) => {
        const mergeRecordWithDetail = (record, detailItem) => ({
          ...record,
          ...detailItem,
          fincasPlan: {
            ...(record.fincasPlan || {}),
            ...(detailItem.fincasPlan || {}),
            start_date: detailItem.start_date ?? record.start_date ?? record.fincasPlan?.start_date ?? "",
            end_date: detailItem.end_date ?? record.end_date ?? record.fincasPlan?.end_date ?? "",
            reentry_date: detailItem.reentry_date ?? record.reentry_date ?? record.fincasPlan?.reentry_date ?? "",
            reentry_time: detailItem.reentry_time ?? record.reentry_time ?? record.fincasPlan?.reentry_time ?? "",
          },
          product: detailItem.product || record.product,
          detailMeasure: detailItem.detailMeasure || record.detailMeasure,
          applicationMode: detailItem.applicationMode || record.applicationMode,
          EPA: detailItem.EPA ?? detailItem.EPA_number ?? detailItem.epa_number ?? detailItem.product?.EPA ?? detailItem.product?.EPA_number ?? detailItem.product?.epa_number ?? record.EPA ?? record.EPA_number ?? record.epa_number ?? record.product?.EPA ?? record.product?.EPA_number ?? record.product?.epa_number ?? "",
          ingredients: detailItem.ingredients ?? detailItem.product?.ingredients ?? record.ingredients ?? record.product?.ingredients ?? "",
          sprayer: detailItem.sprayer ?? record.sprayer ?? "",
        });

        const expandableKeys = [
          "idfinca_plan_detail",
          "idfinca_product",
          "idfinca_application_mode",
          "dose",
          "dose_per_acre",
          "tanks_applied",
          "unit_cost",
          "total_cost",
          "total_cost_per_acre",
          "total_product_cost_per_acre",
          "product",
          "detailMeasure",
          "applicationMode",
          "created_at",
        ];

        return records.flatMap((record) => {
          if (Array.isArray(record.relatedRecords) && record.relatedRecords.length > 0) {
            return record.relatedRecords;
          }

          const explicitDetailArray = record.fincasPlanDetails || record.planDetails || record.details || record.detail;

          if (Array.isArray(explicitDetailArray) && explicitDetailArray.length > 0) {
            return explicitDetailArray.map((detailItem) => mergeRecordWithDetail(record, detailItem));
          }

          const nestedArrayKey = Object.keys(record).find((key) => {
            const value = record[key];
            return (
              Array.isArray(value) &&
              value.length > 0 &&
              value.every(
                (item) =>
                  item &&
                  typeof item === "object" &&
                  ("idfinca_plan_detail" in item ||
                    "idfinca_product" in item ||
                    "dose" in item)
              )
            );
          });

          if (!nestedArrayKey) {
            const maxLength = Math.max(
              0,
              ...expandableKeys.map((key) => (Array.isArray(record[key]) ? record[key].length : 0))
            );
            if (!maxLength) return [record];
            return Array.from({ length: maxLength }, (_, index) => {
              const expandedRecord = { ...record };
              expandableKeys.forEach((key) => {
                if (Array.isArray(record[key])) {
                  expandedRecord[key] = record[key][index] ?? record[key][0] ?? null;
                }
              });
              ["product", "detailMeasure", "applicationMode"].forEach((key) => {
                if (Array.isArray(record[key])) {
                  expandedRecord[key] = record[key][index] ?? record[key][0] ?? null;
                }
              });
              return expandedRecord;
            });
          }
          return record[nestedArrayKey].map((detail) => mergeRecordWithDetail(record, detail));
        });
      };

      //Agrupar los datos solo si el plan es por Cultivo
      const currentPlanType = state.filter.plan_type || 1;
      const planExtra = JSON.parse(localStorage.getItem("@fincas_plan_extra") || "{}");
      const normalizedData = normalizeSectionRows(planDetailResponse.data).map((row) => {
        return {
          ...row,
          EPA: row.EPA || row.product?.EPA || row.product?.EPA_number || row.product?.epa_number || "",
          ingredients: row.ingredients || row.product?.ingredients || "",
        };
      });

      const farmsLookup = (Array.isArray(farmsResponse?.data) ? farmsResponse.data : []).reduce((acc, farm) => {
        if (farm?.idfinca_farm != null) {
          acc[String(farm.idfinca_farm)] = farm;
        }
        return acc;
      }, {});

      const sectionsLookup = (Array.isArray(sectionsResponse?.data) ? sectionsResponse.data : []).reduce((acc, section) => {
        if (section?.idfinca_section != null) {
          acc[String(section.idfinca_section)] = section;
        }
        return acc;
      }, {});

      const areasLookup = (Array.isArray(areasResponse?.data) ? areasResponse.data : []).reduce((acc, area) => {
        if (area?.idfinca_area != null) {
          acc[String(area.idfinca_area)] = area;
        }
        return acc;
      }, {});

      const cultivationsLookup = (Array.isArray(cultivationTypesResponse?.data) ? cultivationTypesResponse.data : []).reduce((acc, cultivation) => {
        if (cultivation?.idfinca_cultivation_type != null) {
          acc[String(cultivation.idfinca_cultivation_type)] = cultivation;
        }
        return acc;
      }, {});

      const enrichRecord = (record) => {
        const sectionId = getSectionId(record);
        const areaId = getAreaId(record);
        const farmId = getFarmId(record, sectionsLookup);
        const cultivationId = getCultivationId(record, sectionsLookup);

        const section = sectionId != null ? sectionsLookup[String(sectionId)] : null;
        const area = areaId != null ? areasLookup[String(areaId)] : null;
        const farm = farmId != null ? farmsLookup[String(farmId)] : null;
        const cultivation = cultivationId != null ? cultivationsLookup[String(cultivationId)] : null;

        const farmName =
          record?.fincasPlan?.farm?.name ||
          record?.farm?.name ||
          record?.area?.farm?.name ||
          section?.farm?.name ||
          section?.area?.farm?.name ||
          area?.farm?.name ||
          farm?.name ||
          "";

        const sectionName =
          record?.fincasPlan?.section?.name ||
          record?.section?.name ||
          record?.fincasSection?.name ||
          record?.fincaSection?.name ||
          section?.name ||
          "";

        const cultivationName =
          record?.cultivationType?.name ||
          record?.fincasCultivationType?.name ||
          record?.fincaCultivationType?.name ||
          record?.fincasPlan?.section?.cultivationType?.name ||
          section?.cultivationType?.name ||
          cultivation?.name ||
          "";

        const distributedAcres =
          section?.acres != null
            ? toNumber(section.acres)
            : area?.acres != null
              ? toNumber(area.acres)
              : toNumber(record?.fincasPlan?.total_acres ?? record?.total_acres);

        const enrichedRecord = {
          ...record,
          resolved_section_id: sectionId,
          resolved_area_id: areaId,
          resolved_farm_id: farmId,
          farm_name: farmName,
          section_name: sectionName,
          cultivation_name: cultivationName,
          distributed_acres: distributedAcres,
          latitude:
            record?.latitude ??
            section?.latitude ??
            area?.latitude ??
            "",
          longitude:
            record?.longitude ??
            section?.longitude ??
            area?.longitude ??
            "",
        };

        const extra = planExtra[`${farmId}_${record.fincasPlan?.date}`];
        if (extra) {
          Object.assign(enrichedRecord, extra);
          enrichedRecord.fincasPlan = { ...(enrichedRecord.fincasPlan || {}), ...extra };
        }


        return {
          ...enrichedRecord,
          _planAggregationKey: getPlanAggregationKey(enrichedRecord),
          _productAggregationKey: getProductAggregationKey(enrichedRecord),
        };
      };
      const enrichedNormalizedData = normalizedData.map(enrichRecord);
      let displayData = enrichedNormalizedData;

      if (currentPlanType === 2) {
        const groupedMap = new Map();
        
        enrichedNormalizedData.forEach((record) => {
          const key = `${record.fincasPlan?.date}_${record.fincasPlan?.idfinca_farm}_${record.fincasPlan?.idfinca_area}_${record.fincasPlan?.idfinca_section}_${record.idfinca_product}_${record.dose}_${record.dose_per_acre}`;

          const existing = groupedMap.get(key);
          existing ? existing.relatedRecords.push(record) : groupedMap.set(key, { ...record, relatedRecords: [record] });
        });
        
        displayData = Array.from(groupedMap.values());
      }

      const acresAccumulator = enrichedNormalizedData.reduce(
        (acc, record, index) => {
          const planKey = record._planAggregationKey;
          const sectionKey =
            record?.resolved_section_id != null
              ? `section_${record.resolved_section_id}`
              : record?.resolved_area_id != null
                ? `area_${record.resolved_area_id}`
                : `row_${record?.idfinca_plan_detail ?? index}`;

          const uniqueKey = `${planKey}__${sectionKey}`;

          if (acc.seenSectionKeys.has(uniqueKey)) return acc;

          acc.seenSectionKeys.add(uniqueKey);
          const current = acc.totalsByPlanKey.get(planKey) || 0;
          acc.totalsByPlanKey.set(planKey, current + getDistributedAcres(record));

          return acc;
        },
        { totalsByPlanKey: new Map(), seenSectionKeys: new Set() }
      );

      const acresTotalsByPlanKey = acresAccumulator.totalsByPlanKey;

      const doseAndTanksTotalsByProductKey = enrichedNormalizedData.reduce((acc, record) => {
        const key = record._productAggregationKey;
        const current = acc.get(key) || { totalDose: 0, totalTanks: 0 };

        current.totalDose += toNumber(record?.dose);
        current.totalTanks += toNumber(record?.tanks_applied);

        acc.set(key, current);
        return acc;
      }, new Map());

      const withAggregatedTotals = displayData.map((record) => {
        const planKey = record._planAggregationKey;
        const productKey = record._productAggregationKey;
        const totalAcres = acresTotalsByPlanKey.get(planKey) || 0;
        const productTotals = doseAndTanksTotalsByProductKey.get(productKey) || { totalDose: 0, totalTanks: 0 };

        return {
          ...record,
          total_acres_all_sections: totalAcres,
          total_dose_all_sections: productTotals.totalDose,
          total_tanks_all_sections: productTotals.totalTanks,
          relatedRecords: Array.isArray(record.relatedRecords)
            ? record.relatedRecords.map((relatedRecord) => ({
              ...relatedRecord,
              total_acres_all_sections: totalAcres,
              total_dose_all_sections: productTotals.totalDose,
              total_tanks_all_sections: productTotals.totalTanks,
            }))
            : record.relatedRecords,
        };
      });

      setState((prev) => ({
        ...prev,
        data: withAggregatedTotals,
        plan_type: planTypeResponse.data,
      }));

      if (planDetailResponse.data.length === 0 && filtering) showToast("There is no data with the selected filters", "warning");
    } catch (error) {
      showToast("Error fetching data:", "error");
    } finally {
      showLoader(false);
    }
  };

  const childRef = useRef();

  const handleChange = (name, value) => {
    setState((prevState) => ({
      ...prevState,
      filter: {
        ...prevState.filter,
        [name]: value,
      },
    }));
    if (childRef.current) {
      childRef.current.clearForm();
    }
  };

  // Enhanced flattenObject to support array fields like "product.fincasApplicationModes[].name"
  const flattenObject = (obj, parentKey = "", sep = ".") => {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      const newKey = parentKey ? `${parentKey}${sep}${key}` : key;
      if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
      ) {
        Object.assign(acc, flattenObject(value, newKey, sep));
      } else if (Array.isArray(value)) {
        // For arrays, flatten each item and join by comma if they are primitives
        acc[newKey] = value
          .map((item) => {
            if (typeof item === "object" && item !== null) {
              // If the array contains objects, join their values by comma
              return Object.values(item).join(", ");
            }
            return item;
          })
          .join(", ");
      } else {
        acc[newKey] = value;
      }
      return acc;
    }, {});
  };

  const generateExcel = async () => {
    const authUser = JSON.parse(localStorage.getItem("@auth_user") || "null");
    const generatedBy = authUser
      ? `${authUser.names ?? ""} ${authUser.surnames ?? ""}`.trim() || authUser.username || ""
      : "";

    const baseColumnOrder = [
      "idfinca_plan_detail",
      "generated_by",
      "fincasPlan.date",
      "start_date",
      "end_date",
      "reentry_date",
      "reentry_time",
      "fincasPlan.week",
      "product.fincasProductType.name",
      "section_name",
      "cultivation_name",
      "distributed_acres",
      "acres_percentage",
      "product.name",
      "detailMeasure.unit",
      "applicationMode.name",
      "EPA",
      "ingredients",
      "sprayer",
      "wind_speed_and_direction",
      "total_dose_all_sections",
      "dose",
      "product.fincasProductSedes[].total_presentation",
      "total_tanks_all_sections",
      "tanks_applied",
      "unit_cost",
      "total_cost",
      "total_product_cost_per_acre",
      "latitude",
      "longitude",
      "total_cost_per_acre",
    ];

    const hiddenFloridaOnlyColumns = [
      "start_date",
      "end_date",
      "reentry_date",
      "reentry_time",
      "EPA",
      "ingredients",
      "sprayer",
    ];

    const columnOrder = containsFlorida
      ? baseColumnOrder
      : baseColumnOrder.filter(
        (columnName) => !hiddenFloridaOnlyColumns.includes(columnName)
      );

    const columnHeaders = {
      idfinca_plan_detail: "ID",
      generated_by: "Generated By",
      "fincasPlan.date": "Date",
      start_date: "Start",
      end_date: "End",
      reentry_date: "Re-Entry Date",
      reentry_time: "Re-Entry Time",
      "fincasPlan.week": "Week",
      "product.fincasProductType.name": "Type",
      section_name: "Section",
      cultivation_name: "Cultivation",
      distributed_acres: "Acres",
      acres_percentage: "Acres %",
      "product.name": "Product Name",
      "detailMeasure.unit": "Unit",
      "applicationMode.name": "Application Mode",
      EPA: "EPA #",
      ingredients: "Ingredients",
      sprayer: "Sprayer",
      wind_speed_and_direction: "Wind Speed and Directions",
      total_dose_all_sections: "Total Dose",
      dose: "Dose/Cultivation",
      "product.fincasProductSedes[].total_presentation": "Total Qty",
      total_tanks_all_sections: "Total Tanks",
      tanks_applied: "Tanks/Cultivation",
      unit_cost: "Unit Cost",
      total_cost: "Total Cost",
      total_product_cost_per_acre: "T. Cost Product Used",
      latitude: "Latitude",
      longitude: "Longitude",
      total_cost_per_acre: "T. Cost/Acre",
    };

    try {
      const flattenedData = state.data.map((item) => {
        const flatItem = flattenObject(item);
        const customFields = {
          generated_by: generatedBy,
          "fincasPlan.date": formatDateToUS(item?.fincasPlan?.date),
          start_date: formatDateToUS(item?.fincasPlan?.start_date ?? item?.start_date),
          end_date: formatDateToUS(item?.fincasPlan?.end_date ?? item?.end_date),
          reentry_date: formatDateToUS(item?.fincasPlan?.reentry_date ?? item?.reentry_date),
          reentry_time: item?.fincasPlan?.reentry_time ?? item?.reentry_time ?? "",
          farm_name: getFarmLabel(item),
          section_name: getSectionLabel(item),
          cultivation_name: getCultivationLabel(item),
          longitude: getLongitude(item),
          latitude: getLatitude(item),
          distributed_acres: getDistributedAcres(item),
          total_acres_all_sections: item.total_acres_all_sections,
          acres_percentage: getAcresPercentage(item),
          total_dose_all_sections: item.total_dose_all_sections,
          total_tanks_all_sections: item.total_tanks_all_sections,
          wind_speed_and_direction: "",
          EPA: item?.EPA ?? item?.EPA_number ?? item?.epa_number ?? item?.detail?.EPA ?? item?.detail?.EPA_number ?? item?.detail?.epa_number ?? "",
          EPA_number: item?.EPA_number ?? item?.detail?.EPA_number ?? item?.detail?.epa_number ?? "",
          ingredients: item?.ingredients ?? item?.detail?.ingredients ?? "",
          sprayer: item?.sprayer ?? item?.detail?.sprayer ?? "",
        };

        const orderedItem = {};
        columnOrder.forEach((col) => {
          // Handle array fields like "product.fincasApplicationModes[].name"
          if (col.includes("[]")) {
            // e.g., "product.fincasApplicationModes[].name"
            const [arrayPath, prop] = col.split("[].");
            const arr = arrayPath.split('.').reduce((o, k) => (o ? o[k] : undefined), item);
            if (Array.isArray(arr)) {
              orderedItem[col] = arr.map((el) => el && el[prop] ? el[prop] : "").filter(Boolean).join(", ");
            } else {
              orderedItem[col] = "";
            }
          } else if (Object.prototype.hasOwnProperty.call(customFields, col)) {
            orderedItem[col] = customFields[col] ?? "";
          } else {
            orderedItem[col] = flatItem[col] || "";
          }
        });
        return orderedItem;
      });

      const worksheet = XLSX.utils.json_to_sheet(flattenedData);

      const headerRow = columnOrder.reduce((acc, col, idx) => {
        acc[col] = columnHeaders[col] || col;
        return acc;
      }, {});

      XLSX.utils.sheet_add_aoa(worksheet, [Object.values(headerRow)], {
        origin: "A1",
      });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

      XLSX.writeFile(workbook, `report_${Date.now()}.xlsx`);
    } catch (error) {
      console.error("Error generating Excel:", error);
    }
  };

  const columns = [
    {
      field: "idfinca_plan_detail",
      headerName: "ID",
      flex: 4,
      minWidth: 90,
    },
    {
      field: "date",
      headerName: "Date",
      flex: 4,
      minWidth: 110,
      valueGetter: (params) => {
        const fincasPlan = params.row.fincasPlan;
        return formatDateToUS(fincasPlan?.date);
      },
    },
    {
      field: "week",
      headerName: "Week",
      flex: 4,
      minWidth: 100,
      valueGetter: (params) => {
        const fincasPlan = params.row.fincasPlan;
        return fincasPlan.week;
      },
    },
    {
      field: "farm_name",
      headerName: "Farm",
      flex: 6,
      minWidth: 120,
      valueGetter: (params) => getFarmLabel(params.row),
    },
    {
      field: "section_name",
      headerName: "Section",
      flex: 6,
      minWidth: 120,
      valueGetter: (params) => getSectionLabel(params.row),
    },
    {
      field: "cultivation_name",
      headerName: "Cultivation",
      flex: 6,
      minWidth: 130,
      valueGetter: (params) => getCultivationLabel(params.row),
    },
    {
      field: "acres",
      headerName: "Acres",
      flex: 4,
      minWidth: 100,
      valueGetter: (params) => getDistributedAcres(params.row),
      valueFormatter: (params) => {
        const value = Number(params.value);
        return !isNaN(value)
          ? value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,")
          : "";
      },
    },
    {
      field: "total_acres_all_sections",
      headerName: "Total Acres",
      flex: 6,
      minWidth: 130,
      valueFormatter: (params) => {
        const value = Number(params.value);
        return !isNaN(value)
          ? value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,")
          : "";
      },
    },
    {
      field: "acres_percentage",
      headerName: "%",
      flex: 5,
      minWidth: 100,
      valueGetter: (params) => getAcresPercentage(params.row),
      valueFormatter: (params) => {
        const value = Number(params.value);
        return !isNaN(value) ? `${value.toFixed(2)}%` : "";
      },
    },
    {
      field: "product",
      headerName: "Product",
      flex: 6,
      minWidth: 150,
      valueGetter: (params) => {
        const product = params.row.product;
        return product.name;
      },
    },
    {
      field: "start_date",
      headerName: "Start",
      flex: 4,
      minWidth: 110,
      valueGetter: (params) => formatDateToUS(params.row.fincasPlan?.start_date ?? params.row.start_date),
    },
    {
      field: "end_date",
      headerName: "End",
      flex: 4,
      minWidth: 110,
      valueGetter: (params) => formatDateToUS(params.row.fincasPlan?.end_date ?? params.row.end_date),
    },
    {
      field: "reentry_date",
      headerName: "Re-Entry Date",
      flex: 5,
      minWidth: 130,
      valueGetter: (params) => formatDateToUS(params.row.fincasPlan?.reentry_date ?? params.row.reentry_date),
    },
    {
      field: "reentry_time",
      headerName: "Re-Entry Time",
      flex: 5,
      minWidth: 130,
      valueGetter: (params) => params.row.fincasPlan?.reentry_time ?? params.row.reentry_time ?? "",
    },
    {
      field: "measure",
      headerName: "Unit",
      flex: 5,
      minWidth: 95,
      valueGetter: (params) => {
        return params.row.detailMeasure?.unit || "";
      },
    },
    {
      field: "application_type",
      headerName: "App. Type",
      flex: 5,
      minWidth: 140,
      valueGetter: (params) => {
        return params.row.applicationMode?.name || "";
      },
    },
    {
      field: "EPA",
      headerName: "EPA #",
      flex: 5,
      minWidth: 140,
      valueGetter: (params) => params.row.EPA ?? params.row.EPA_number ?? params.row.epa_number ?? params.row.detail?.EPA ?? params.row.detail?.EPA_number ?? params.row.detail?.epa_number ?? "",
    },
    {
      field: "ingredients",
      headerName: "Ingredients",
      flex: 5,
      minWidth: 160,
      valueGetter: (params) => params.row.ingredients ?? params.row.detail?.ingredients ?? "",
    },
    {
      field: "sprayer",
      headerName: "Sprayer",
      flex: 5,
      minWidth: 160,
      valueGetter: (params) => params.row.sprayer ?? params.row.detail?.sprayer ?? "",
    },
    {
      field: "type",
      headerName: "Product Type",
      flex: 5,
      minWidth: 150,
      valueGetter: (params) => {
        const fincasProductType = params.row.product.fincasProductType;
        return fincasProductType.name;
      },
    },
    {
      field: "dose",
      headerName: "Dose",
      flex: 4,
      minWidth: 100,
      valueFormatter: (params) => {
        const value = Number(params.value);
        return !isNaN(value)
          ? value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,")
          : "";
      },
    },
    {
      field: "dose_per_acre",
      headerName: "D/Acre",
      flex: 6,
      minWidth: 120,
      valueFormatter: (params) => {
        const value = Number(params.value);
        return !isNaN(value)
          ? value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,")
          : "";
      },
    },
    {
      field: "tanks_applied",
      headerName: "Tanks Applied",
      flex: 6,
      minWidth: 150,
      valueFormatter: (params) => {
        const value = Number(params.value);
        return !isNaN(value)
          ? value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,")
          : "";
      },
    },
    {
      field: "total_dose_all_sections",
      headerName: "Total Dose",
      flex: 6,
      minWidth: 130,
      valueFormatter: (params) => {
        const value = Number(params.value);
        return !isNaN(value)
          ? value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,")
          : "";
      },
    },
    {
      field: "total_tanks_all_sections",
      headerName: "Total Tanks",
      flex: 6,
      minWidth: 140,
      valueFormatter: (params) => {
        const value = Number(params.value);
        return !isNaN(value)
          ? value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,")
          : "";
      },
    },
    {
      field: "unit_cost",
      headerName: "Unit Cost",
      flex: 6,
      minWidth: 130,
      valueFormatter: (params) => {
        const value = Number(params.value);
        return !isNaN(value)
          ? `$ ${value.toFixed(4).replace(/\d(?=(\d{3})+\.)/g, "$&,")}`
          : "";
      },
    },
    {
      field: "total_cost",
      headerName: "Total Cost/Cultivation",
      flex: 4,
      minWidth: 180,
      valueFormatter: (params) => {
        const value = Number(params.value);
        return !isNaN(value)
          ? `$ ${value.toFixed(4).replace(/\d(?=(\d{3})+\.)/g, "$&,")}`
          : "";
      },
    },
    {
      field: "total_cost_per_acre",
      headerName: "T. Cost/Acre",
      flex: 6,
      minWidth: 140,
      valueFormatter: (params) => {
        const value = Number(params.value);
        return !isNaN(value)
          ? `$ ${value.toFixed(4).replace(/\d(?=(\d{3})+\.)/g, "$&,")}`
          : "";
      },
    },
    {
      field: "total_product_cost_per_acre",
      headerName: "Total P. Cost/Acre",
      flex: 5,
      minWidth: 170,
      valueFormatter: (params) => {
        const value = Number(params.value);
        return !isNaN(value)
          ? `$ ${value.toFixed(4).replace(/\d(?=(\d{3})+\.)/g, "$&,")}`
          : "";
      },
    },
    {
      field: "history",
      headerName: "Actions",
      flex: 6,
      minWidth: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Link to={`/history/fincas_plan_detail/${params.row.idfinca_plan_detail}`} target="_blank">
          <Tooltip title="Show history" placement="top">
            <IconButton>
              <Icon>history</Icon>
            </IconButton>
          </Tooltip>
        </Link>
      ),
    },
  ];

  const hiddenFloridaOnlyFields = [
    "start_date",
    "end_date",
    "reentry_date",
    "reentry_time",
    "EPA",
    "ingredients",
    "sprayer",
  ];

  const visibleColumns = containsFlorida
    ? columns
    : columns.filter((column) => !hiddenFloridaOnlyFields.includes(column.field));

  const gridColumns = visibleColumns.map((column) => ({
    ...column,
    width: column.width || column.minWidth || 120,
    flex: undefined,
  }));

  const updateData = () => {
    makeRequest();
  };

  return (
    <div className={classes.root}>
      <ViewTitle sedeFlag title="Application Plan" />
      <Divider className={classes.divider} />
      <Grid container>
        <Grid item xs={12} sm={6} lg={3}>
          <FormGroup>
            <FormLabel sx={{ marginBottom: "4px" }}>{"Plan Type"}</FormLabel>
            <FormControl variant="filled" component={Box} width="100%">
              <Select
                value={state.filter.plan_type}
                name="plan_type"
                onChange={(event) =>
                  handleChange("plan_type", event.target.value)
                }
                displayEmpty
                input={<FilledInput />}
                sx={{
                  backgroundColor: "inherit",
                  ":hover": {
                    backgroundColor: "inherit",
                  },
                }}
              >
                {!containsOregon
                  ? state.plan_type.map((option) => (
                    <MenuItem
                      key={option.idfinca_plan_type}
                      value={option.idfinca_plan_type}
                    >
                      {option.name}
                    </MenuItem>
                  ))
                  : state.plan_type
                    .filter((option) => option.name === "Section")
                    .map((option) => (
                      <MenuItem
                        key={option.idfinca_plan_type}
                        value={option.idfinca_plan_type}
                      >
                        {option.name}
                      </MenuItem>
                    ))}
              </Select>
            </FormControl>
          </FormGroup>
        </Grid>
      </Grid>
      <Divider className={classes.divider} />

      <PerimeterForm
          ref={childRef}
          idfinca_plan_type={state.filter.plan_type}
          onSave={updateData}
          selectedRow={state.rowSelected}
          setSelectedRow={setState}
          hideSections={state.filter.plan_type === 3}
          cultivations={state.filter.plan_type === 2}
        />

      <Divider className={classes.divider} />
      <SearchForm makeRequestParentFunction={makeRequest} />
      <div className={classes.buttonsContainer}>
        <Button
          variant="contained"
          className={classes.generateExcelButton}
          onClick={generateExcel}
          disabled={state.data[0] ? false : true}
        >
          {"Download Report"}
        </Button>
      </div>
      <div className={classes.tableContainerTab}>
        <DataGrid
          className={classes.controlTable}
          rows={state.data}
          columns={gridColumns}
          pageSize={20}
          rowsPerPageOptions={[5, 10, 20, 50]}
          density="compact"
          autoHeight
          getRowId={(row) => row.idfinca_plan_detail}
          onRowClick={(params) => {
            const clickedRow = params.row;
            const relatedRecords = clickedRow.relatedRecords || [clickedRow];
            const allSections = relatedRecords.map((r) => r.fincasPlan?.idfinca_section).filter(Boolean);
            const enhancedRow = {
              ...clickedRow,
              fincasPlan: { ...clickedRow.fincasPlan, sections: allSections },
              relatedRecords,
            };

            setState((prev) => ({
              ...prev,
              rowSelected:
                prev.rowSelected?.idfinca_plan_detail ===
                  clickedRow.idfinca_plan_detail
                  ? null
                  : enhancedRow,
            }));
          }}
        />
      </div>
    </div>
  );
};

export default Control;
